import type { Column } from "./types";

/**
 * We use the column reference to generate unique aliases that can be injected within
 * the formula trait SQL.
 * These aliases will be used in the generated warehouse SQL, so we have to be cognizant
 * of using legal characters. The tested warehouses accept alphanumeric characters,
 * we'll use those.
 *
 * SHA-256 returns 64 characters.
 * When we generate the SQL in the backend, we also append a suffix to the alias to make it unique.
 * Most warehouses have a max alias length of ~64 characters. Therefore, we should ensure that the
 * ones we generate are well below this limit.
 * Even with truncation, hexadecimal gives each character 16 possibilities, so that should
 * still be unique enough to avoid collisions with other aliases generated via this helper.
 *
 * The hash is generated by:
 * 1. Deep-sorting object keys for consistent serialization
 * 2. Creating a SHA-256 hash of the JSON string
 * 3. Taking the first 8 characters of the hex digest
 *
 * @param columnReference - The column reference to generate an alias for
 * @param hashFn - A function that takes a string and returns a SHA-256 hex digest. This is parametrized because the
 *  frontend and backend don't have access to the same crypto libs, but we want to share the logic
 * @param opts - Optional parameters
 * @returns An 8-character hexadecimal string (e.g., "bfaa7660", "6d3d2043")
 */
export const getColumnAlias = (
  columnReference: Column,
  hashFn: (input: string) => string,
): string => {
  // Deep sort object keys to ensure consistent string representation
  // This is critical because JavaScript object key ordering isn't guaranteed
  const deepSortKeys = (_key: string, value: unknown): unknown => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return Object.keys(value)
        .sort()
        .reduce((sortedObj: Record<string, unknown>, currentKey) => {
          sortedObj[currentKey] = (value as Record<string, unknown>)[
            currentKey
          ];
          return sortedObj;
        }, {});
    }
    return value;
  };

  // Generate SHA-256 hash and return first 8 characters
  const jsonString = JSON.stringify(columnReference, deepSortKeys);
  return hashFn(jsonString).slice(0, 8);
};
